import { RPCDocs, RPCs } from "../../../../services/rpc.js";
import { NoResults } from "../../../../components/NoResults/NoResults.js";
import { useRPC } from "../../../../hooks/useRPC/index.js";
import { usePaginationState } from "../../../../hooks/usePaginationState/index.js";
import { recordIcons } from "../record-icons.js";
import { RecordSearchFilters, recordSearchFiltersDefaultValues } from "../RecordSearchFilters/index.js";
import { RecordListCard } from "../RecordListCard.js";
import { PickedRecordsModal } from "./components/PickedRecordsModal.js";
import { CreateRecordModal } from "../CreateRecordModal/index.js";
import { useReducer, useState } from "react";
import { Button, ButtonGroup, Icon, Loading, Modal, Pagination, Tabs } from "@therms/atalaya";
import { IoMdAdd } from "react-icons/io";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
import { pluralize } from "@therms/web-js";
import { useUpdateEffect } from "ahooks";
//#region src/modules/Records/shared/RecordPickModal/index.tsx
var RecordTypes = RPCDocs.Records.GetRecordsList.v1.TypeFilter;
var reducer = (state, { multi, recordId }) => {
	if (multi) {
		const set = new Set(state);
		if (set.has(recordId)) set.delete(recordId);
		else set.add(recordId);
		return Array.from(set);
	} else if (state[0] === recordId) return [];
	else return [recordId];
};
function RecordPickModal({ closeHandler, multi, onSubmit, recordIds }) {
	const [tab, setTab] = useState(RecordTypes.Person);
	const [filters, setFilters] = useState(recordSearchFiltersDefaultValues);
	const { recordsPerPage, setRecordsPerPage, setSkippedRecords, skippedRecords } = usePaginationState("reclist", []);
	const [selectedRecordIds, toggleRecordId] = useReducer(reducer, typeof recordIds === "string" ? [recordIds] : recordIds || []);
	const [isPickedRecordsModalOpen, setIsPickedRecordsModalOpen] = useState(false);
	const [isCreatingRecordModalOpen, setIsCreatingRecordModalOpen] = useState(false);
	useUpdateEffect(() => {
		setSkippedRecords(0);
	}, [filters]);
	const { call, data, isCalling } = useRPC(RPCs.Records.GetRecordsList.v1, {
		fieldsFilter: filters[tab],
		pagination: {
			limit: recordsPerPage,
			skip: skippedRecords
		},
		recordTagIds: filters[tab].tags.length ? filters[tab].tags : void 0,
		searchText: filters.searchText,
		typeFilter: [tab]
	});
	const handleSubmit = (recordIds) => {
		onSubmit(multi ? recordIds : recordIds[0]);
	};
	return /* @__PURE__ */ jsxs(Modal, {
		header: /* @__PURE__ */ jsxs("div", {
			className: "gap-sm mr-xs flex justify-between",
			children: [/* @__PURE__ */ jsx("span", {
				className: "text-color-subtle text-lg font-medium",
				children: "Select Records"
			}), /* @__PURE__ */ jsxs(Button, {
				onClick: () => setIsCreatingRecordModalOpen(true),
				variant: "main",
				size: "sm",
				children: [
					/* @__PURE__ */ jsx(Icon, { Icon: IoMdAdd }),
					" ",
					/* @__PURE__ */ jsx("span", { children: "New Record" })
				]
			})]
		}),
		closeHandler,
		children: [
			/* @__PURE__ */ jsx(RecordSearchFilters, {
				onChange: (filters) => setFilters(filters),
				tab
			}),
			/* @__PURE__ */ jsx("hr", { className: "border-border mt-base" }),
			/* @__PURE__ */ jsxs(Tabs, {
				fullWidth: true,
				initialTabKey: tab,
				onTabClick: (newTab) => {
					setSkippedRecords(0);
					setTab(newTab);
				},
				children: [
					/* @__PURE__ */ jsx(Tabs.Tab, {
						tabKey: "item",
						title: "Items"
					}),
					/* @__PURE__ */ jsx(Tabs.Tab, {
						tabKey: "person",
						title: "People"
					}),
					/* @__PURE__ */ jsx(Tabs.Tab, {
						tabKey: "vehicle",
						title: "Vehicles"
					})
				]
			}),
			isCalling && !data?.records && /* @__PURE__ */ jsx("div", {
				className: "p-lg flex items-center justify-center",
				children: /* @__PURE__ */ jsx(Loading, { size: "lg" })
			}),
			!!data?.records?.length && /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsx("div", {
				className: "bg-background p-xs space-y-xs rounded",
				children: data.records.map((record) => /* @__PURE__ */ jsx(RecordListCard, {
					active: selectedRecordIds.includes(record.id),
					description: record.description,
					idLabel: record.idLabel,
					lastActivity: record.lastActivity,
					onClick: () => toggleRecordId({
						multi,
						recordId: record.id
					}),
					orientation: "horizontal",
					primaryImageUrl: record.primaryImageUrl,
					tags: record.tags,
					title: record.title
				}, record.id))
			}), /* @__PURE__ */ jsx("div", {
				className: "mt-sm mx-base",
				children: /* @__PURE__ */ jsx(Pagination, {
					onPageClick: setSkippedRecords,
					onSetRecordsPerPage: setRecordsPerPage,
					recordsPerPage,
					showDescription: true,
					skippedRecords,
					totalRecords: data?.total
				})
			})] }),
			!isCalling && !data?.records?.length && /* @__PURE__ */ jsx(NoResults, {
				iconName: recordIcons[tab],
				text: `No ${tab} Records`
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "bg-surface py-sm z-sticky translate-y-base sticky bottom-0 flex justify-between rounded",
				children: [/* @__PURE__ */ jsx("div", { children: !!selectedRecordIds.length && /* @__PURE__ */ jsx(Button, {
					onClick: () => setIsPickedRecordsModalOpen(true),
					subtle: true,
					fullWidth: true,
					variant: "main",
					children: multi ? `${selectedRecordIds.length} ${pluralize("Record", selectedRecordIds.length)} Selected` : "Selected Record"
				}) }), /* @__PURE__ */ jsxs(ButtonGroup, { children: [/* @__PURE__ */ jsx(Button, {
					onClick: closeHandler,
					children: "Cancel"
				}), /* @__PURE__ */ jsx(Button, {
					onClick: () => {
						handleSubmit(selectedRecordIds);
						closeHandler();
					},
					variant: "main",
					children: "Submit"
				})] })]
			}),
			isPickedRecordsModalOpen && /* @__PURE__ */ jsx(PickedRecordsModal, {
				onRecordDelete: (recordId) => toggleRecordId({
					multi,
					recordId
				}),
				closeHandler: () => setIsPickedRecordsModalOpen(false),
				recordIds: selectedRecordIds
			}),
			isCreatingRecordModalOpen && /* @__PURE__ */ jsx(CreateRecordModal, {
				closeHandler: () => setIsCreatingRecordModalOpen(false),
				onComplete: (recordId) => {
					if (recordId) toggleRecordId({
						multi,
						recordId
					});
					call();
				}
			})
		]
	});
}
//#endregion
export { RecordPickModal };

//# sourceMappingURL=index.js.map